import { Component, OnInit } from '@angular/core';
import { CurrencyPipe, DatePipe } from '@angular/common';
import { ProfitAndLossReport } from 'src/app/shared/models/finance';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { HttpErrorResponse } from '@angular/common/http';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { FinanceService } from 'src/app/shared/services/finance.service';
import { DomainService } from 'src/app/shared/services/domain.service';
import { CommonService } from 'src/app/shared/services/common.service';

@Component({
  selector: 'app-profit-loss',
  templateUrl: './profit-loss.component.html',
  styleUrls: ['./profit-loss.component.scss'],
  providers: [DatePipe, CurrencyPipe]
})
export class ProfitLossComponent implements OnInit {
  public report: ProfitAndLossReport = { income: {}, expenses: {} };
  public isLoading = false;
  public selectedFilter = '';
  public filters: string[] = [];
  public fromDate: Date;
  public toDate: Date;
  public totals = { revenue: 0, expenses: 0, netProfit: 0 };
  public formatted = { revenue: '0.00', expenses: '0.00', netProfit: '0.00' };

  constructor(
    private financeService: FinanceService,
    private datePipe: DatePipe,
    private domainService: DomainService,
    private commonService: CommonService,
    private currencyPipe: CurrencyPipe,
  ) { }

  ngOnInit(): void {
    this.filters = this.domainService.getDateFiltersTypes();
    this.selectedFilter = this.filters[2] || 'Last Week';
    this.fromDate = this.commonService.adjustDateByDays(-30);
    this.toDate = new Date()
    this.getProfitAndLossReport();
  }

  public filterAndFetchReport(filter: string, dropdown?: NgbDropdown): void {
    const now = new Date();
    const today = new Date(now.setHours(0, 0, 0, 0));
    let fromDate: Date;
    let toDate: Date;
    switch (filter) {
      case 'Today':
        fromDate = new Date(today);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Yesterday':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 1);
        toDate = new Date(today);
        toDate.setDate(today.getDate() - 1);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Last Week':
        fromDate = new Date(today);
        fromDate.setDate(today.getDate() - 7);
        toDate = new Date(today);
        toDate.setHours(23, 59, 59, 999);
        break;
      case 'Custom':
        if (!this.fromDate || !this.toDate) {
          return;
        }
        fromDate = new Date(this.fromDate);
        toDate = new Date(this.toDate);
        toDate.setHours(23, 59, 59, 999);
        break;
      default:
        return;
    }
    dropdown?.close();
    this.fromDate = fromDate;
    this.toDate = toDate;
    this.getProfitAndLossReport();
  }

  private getProfitAndLossReport(): void {
    this.isLoading = true;
    if (!this.fromDate || !this.toDate) {
      this.commonService.setToastr(400, 'Date range required.');
      this.isLoading = false;
      return;
    }
    const from = this.datePipe.transform(this.fromDate, 'dd-MM-yyyy') || '';
    const to = this.datePipe.transform(this.toDate, 'dd-MM-yyyy') || '';
    this.financeService.getProfitAndLossReport(from, to).subscribe({
      next: (report: ProfitAndLossReport) => {
        this.report = report;
        this.totals.revenue = this.calculateTotal(report.income);
        this.totals.expenses = this.calculateTotal(report.expenses);
        this.totals.netProfit = this.totals.revenue - this.totals.expenses;
        this.isLoading = false;
      },
      error: (err: HttpErrorResponse) => {
        console.error('Error:', err);
        this.commonService.setToastr(500, err?.error?.name || 'Failed to fetch report.');
        this.isLoading = false;
      }
    });
  }

  private calculateTotal(section: { [key: string]: number }): number {
    return Object.values(section).reduce((sum, val) => sum + (val || 0), 0);
  }

  public downloadExcel(): void {
    if (this.isEmptyReport()) {
      this.commonService.setToastr(400, 'No data to export.');
      return;
    }
    const data: any[] = [
      { Category: `Date Range: ${this.datePipe.transform(this.fromDate, 'dd-MM-yyyy')} to ${this.datePipe.transform(this.toDate, 'dd-MM-yyyy')}`, Amount: '' },
      {},
      { Category: 'Revenue', Amount: '' },
      ...Object.entries(this.report.income).map(([key, value]) => ({
        Category: key,
        Amount: this.currencyPipe.transform(value, 'INR', 'symbol', '1.2-2', 'en-IN')
      })),
      { Category: 'Total Revenue', Amount: this.currencyPipe.transform(this.totals.revenue, 'INR', 'symbol', '1.2-2', 'en-IN') },
      {},
      { Category: 'Expenses', Amount: '' },
      ...Object.entries(this.report.expenses).map(([key, value]) => ({
        Category: key,
        Amount: this.currencyPipe.transform(value, 'INR', 'symbol', '1.2-2', 'en-IN')
      })),
      { Category: 'Total Expenses', Amount: this.currencyPipe.transform(this.totals.expenses, 'INR', 'symbol', '1.2-2', 'en-IN') },
      {},
      { Category: 'Net Profit/Loss', Amount: this.currencyPipe.transform(this.totals.netProfit, 'INR', 'symbol', '1.2-2', 'en-IN') }
    ];
    const ws: XLSX.WorkSheet = XLSX.utils.json_to_sheet(data);
    ws['!cols'] = [{ wch: 30 }, { wch: 15 }];
    const wb: XLSX.WorkBook = { Sheets: { 'Profit and Loss': ws }, SheetNames: ['Profit and Loss'] };
    const buffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      `Profit_Loss_${this.datePipe.transform(new Date(), 'yyyy-MM-dd')}.xlsx`);
  }

  public printReport(): void {
    window.print();
  }

  public isEmptyReport(): boolean {
    return !Object.keys(this.report.income).length && !Object.keys(this.report.expenses).length;
  }
}